// Owner-facing sending-plan + progress copy for a bulk-SMS batch (plans copy-spec "Review — Sending plan",
// "Progress" table and its chips), shared by the API (gate details, detail status line) and the admin web
// app (plan card, recent-sends chips) so both say exactly the same thing. Pure string builders, no I/O.

import type { BulkSmsHoldReason, BulkSmsPausedReason, BulkSmsSendStatus } from './bulk-sms-batch-state-reasons';
import { MAX_BULK_WAVE_DAYS } from './bulk-sms-limits';
import { formatBulkPeople } from './bulk-sms-status-copy';

const n = (value: number) => value.toLocaleString('en-US');

/** Review "Blocked > 7 days"; a plan that never ends (one text costs more than a whole day) reads "more than 7". */
export function bulkScheduleTooLongLine(days: number, maxPeople: number): string {
  const span = Number.isFinite(days) ? `${n(days)} days` : `more than ${MAX_BULK_WAVE_DAYS} days`;
  return `This would take ${span} — the most is ${MAX_BULK_WAVE_DAYS}. Shorten the message to 1 text or send to fewer people (about ${n(maxPeople)} fit in ${MAX_BULK_WAVE_DAYS} days).`;
}

/** Review "budget used up" — agency admins set the budget themselves; salon staff ask their agency. */
export function bulkBudgetUsedUpLine(budgetEditable: boolean): string {
  return budgetEditable
    ? "Today's budget is used up. Your message can start tomorrow, or raise the daily budget in Settings → Messaging."
    : "Today's budget is used up. Your message can start tomorrow, or ask your agency to raise the daily budget.";
}

/** Whole dollars when even, else cents: 5000 → "$50", 1250 → "$12.50". */
export function formatBulkDollars(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** A whole 24h hour as a short label: 8 → "8 AM", 20 → "8 PM", 0 → "12 AM". */
export function formatBulkHourShort(hour: number): string {
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour % 12 === 0 ? 12 : hour % 12} ${period}`;
}

/** Review "Outside hours": `hours` like "8 AM – 8 PM", `date` "Tue, Oct 14", `time` "8:00 AM". */
export function bulkOutsideHoursLine(input: { hours: string; date: string; time: string }): string {
  return `Sending hours are ${input.hours} salon time. Your message starts ${input.date} at ${input.time}.`;
}

/** Review "Outside hours" on a day the salon's state closes entirely (e.g. a Pennsylvania Sunday). */
export function bulkClosedDayLine(input: { stateName: string; date: string; time: string }): string {
  return `No marketing texts today under ${input.stateName} law. Your message starts ${input.date} at ${input.time}.`;
}

/** The salon has no daily text allowance at all (unreachable with the $5 minimum budget; a safe line). */
export function bulkNoAllowanceLine(budgetEditable: boolean): string {
  return budgetEditable
    ? 'There is no daily text allowance. Raise the daily budget in Settings → Messaging to send.'
    : 'There is no daily text allowance. Ask your agency to raise the daily budget to send.';
}

/** Truthful pace line: the carrier's per-minute pace and the salon's daily bulk allowance. */
export function bulkThrottleLine(perMinute: number, perDay: number): string {
  return `About ${n(perMinute)} texts/min · up to ${n(perDay)}/day for your number`;
}

/** SENDING progress line. */
export function bulkSendingLine(input: { sent: number; total: number; day: number; days: number }): string {
  return `${n(input.sent)} of ${n(input.total)} sent · Day ${n(input.day)} of ${n(input.days)}`;
}

export interface BulkHeldLineInput {
  reason: BulkSmsHoldReason;
  /** Formatted day + salon-local time the next wave starts ("Tue, Oct 14", "10:30 AM"). */
  date: string;
  time: string;
  /** DAILY_LIMIT: texts that went out today. */
  sentToday?: number;
  /** CARRIER_DAILY_CAP: people the carrier refused today who are retried next wave. */
  retry?: number;
}

/** HELD progress line (waits on its own; no owner action needed). */
export function bulkHeldLine(input: BulkHeldLineInput): string {
  switch (input.reason) {
    case 'DAILY_LIMIT':
      return `Today's ${n(input.sentToday ?? 0)} are done. Continues ${input.date} at ${input.time}.`;
    case 'QUIET_HOURS':
      return `Continues ${input.date} at ${input.time}.`;
    case 'CARRIER_DAILY_CAP':
      return `Your carrier's daily limit was reached early. Continues ${input.date} at ${input.time}; ${formatBulkPeople(input.retry ?? 0)} will be retried.`;
    case 'PLAN_LIMIT':
      return `This month's texts and spending limit are used up. Continues ${input.date}, or sooner if you raise your spending limit.`;
    case 'BILLING_INACTIVE':
      return 'Waiting on your Hayven plan payment. Continues as soon as it is sorted out.';
  }
}

/** QUEUED for a later start (Send pressed outside sending hours) — the copy-spec "starts" wording. */
export function bulkStartsLine(input: { date: string; time: string }): string {
  return `Your message starts ${input.date} at ${input.time}.`;
}

/** CANCELLED progress line. */
export function bulkCancelledLine(input: { cancelledOn: string; sent: number; left: number }): string {
  return `Cancelled ${input.cancelledOn} · ${n(input.sent)} sent before cancelling · ${n(input.left)} not sent`;
}

/** The short status chip (detail header + recent sends). */
export function bulkStatusChip(input: {
  status: BulkSmsSendStatus;
  holdReason: BulkSmsHoldReason | null;
  pausedReason: BulkSmsPausedReason | null;
  /** QUEUED / HELD whose next start is still ahead. A HELD batch whose start has passed is about to send
   *  (its worker run is due), so it reads "Sending". */
  waiting?: boolean;
}): string {
  switch (input.status) {
    case 'DRAFT':
      return 'Draft';
    case 'QUEUED':
      return input.waiting ? 'Waiting for sending hours' : 'Sending';
    case 'SENDING':
      return 'Sending';
    case 'HELD':
      if (input.waiting === false) return 'Sending';
      switch (input.holdReason) {
        case 'QUIET_HOURS':
          return 'Waiting for sending hours';
        case 'PLAN_LIMIT':
          return 'Waiting: monthly text limit reached';
        case 'BILLING_INACTIVE':
          return 'Waiting: plan payment';
        default:
          return 'Continues tomorrow';
      }
    case 'PAUSED':
      if (input.pausedReason === 'CARRIER_FILTERING' || input.pausedReason === 'HIGH_OPT_OUT') {
        return 'Paused to protect your number';
      }
      return input.pausedReason === 'PLAN_DOWNGRADED' ? "Paused: campaigns aren't in your plan" : 'Paused';
    case 'COMPLETED':
      return 'Finished';
    case 'CANCELLED':
      return 'Cancelled';
  }
}
